import React from "react"
import styled from "@emotion/styled"
import {
  Box,
  SimpleGrid,
} from "@chakra-ui/react"
import Translation from "./Translation"
import Link from "./Link"

const BoxContainer = styled(Box)`
  color: ${(props) => props.theme.colors.text};
  border-top: 1px solid ${(props) => props.theme.colors.text};
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: bold;
  font-family: Calibri, sans-serif;
`

const FooterSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const SectionTitle = styled(Box)`
  font-weight: bold;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
`

const FooterLink = styled(Link)`
  color: ${(props) => props.theme.colors.text};
  opacity: 0.75;
  font-weight: normal;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`

const sectionLinks = [
  { label: "About", href: "#about" },
  { label: "How to Buy", href: "#how-to-buy" },
  { label: "Tokenomics", href: "#tokenomics" },
  { label: "Roadmap", href: "#roadmap" },
  { label: "Ecosystem", href: "#ecosystem" },
  { label: "FAQ", href: "/faq" },
]

const socialLinks = [
  { label: "Twitter", href: "https://twitter.com/NutcoinOrg", ariaLabel: "Nutcoin Twitter" },
  { label: "Telegram", href: "https://t.me/NutcoinOrg", ariaLabel: "Nutcoin Telegram Channel" },
  { label: "Telegram EN", href: "https://t.me/Nutcoin_EN", ariaLabel: "Nutcoin Telegram English Group" },
  { label: "GitHub", href: "https://github.com/NutcoinOrg", ariaLabel: "Nutcoin GitHub" },
  { label: "Medium", href: "https://medium.com/@nutcoin", ariaLabel: "Nutcoin Medium" },
]

const ecosystemLinks = [
  { label: "wencash.org", href: "https://wencash.org", ariaLabel: "Wencash Layer 1 blockchain" },
  { label: "nuts.family", href: "https://nuts.family", ariaLabel: "NUTS NFTS" },
  { label: "nutpal.ai", href: "https://nutpal.ai", ariaLabel: "Nutpal AI chat companion" },
  { label: "nutcash.org", href: "https://nutcash.org", ariaLabel: "Nutcash DeFi staking" },
  { label: "nutchain.org", href: "https://nutchain.org", ariaLabel: "Nutchain ZK Rollup Layer 2" },
  { label: "nutswap.org", href: "https://nutswap.org", ariaLabel: "Nutswap decentralized exchange" },
  { label: "nutbridge.org", href: "https://nutbridge.org", ariaLabel: "Nutbridge cross-chain bridge" },
  { label: "nutgames.org", href: "https://nutgames.org", ariaLabel: "Nutgames gaming platform" },
]

export interface IProps {}

const Footer: React.FC<IProps> = () => {
  return (
    <BoxContainer as="footer" p="2rem">
      <SimpleGrid
        columns={{ base: 1, sm: 2, lg: 4 }}
        gap="2rem"
        maxW="1200px"
        width="full"
        px="1rem"
      >
        <FooterSection>
          <SectionTitle>Nutcoin</SectionTitle>
          {sectionLinks.map((link) => (
            <FooterLink key={link.href} href={link.href}>
              {link.label}
            </FooterLink>
          ))}
        </FooterSection>

        <FooterSection>
          <SectionTitle>Social</SectionTitle>
          {socialLinks.map((link) => (
            <FooterLink
              key={link.href}
              href={link.href}
              aria-label={link.ariaLabel}
            >
              {link.label}
            </FooterLink>
          ))}
        </FooterSection>

        <FooterSection>
          <SectionTitle>Ecosystem</SectionTitle>
          {ecosystemLinks.map((link) => (
            <FooterLink
              key={link.href}
              href={link.href}
              aria-label={link.ariaLabel}
            >
              {link.label}
            </FooterLink>
          ))}
        </FooterSection>

        <FooterSection>
          <SectionTitle>Track $NUT</SectionTitle>
          <FooterLink href="https://coinmarketcap.com/currencies/nutcoin-org/" aria-label="CoinMarketCap">
            CoinMarketCap
          </FooterLink>
          <FooterLink href="https://www.coingecko.com/en/coins/nutcoin-meme" aria-label="CoinGecko">
            CoinGecko
          </FooterLink>
          <FooterLink href="https://etherscan.io/address/0x473F4068073Cd5b2aB0E4Cc8E146F9EDC6fb52CC" aria-label="Etherscan">
            Etherscan
          </FooterLink>
          <FooterLink href="https://arbiscan.io/address/0x8697841b82c71fcbd9e58c15f6de68cd1c63fd02" aria-label="Arbiscan">
            Arbiscan
          </FooterLink>
          <FooterLink href="https://basescan.org/address/0x6a9431b9ecce9dde3f7a32391d5b61c5ad11e4a0" aria-label="BaseScan">
            Basescan
          </FooterLink>
          <FooterLink href="https://solscan.io/token/3KkQ3AaMs2nZoKvYzGnh5bwRocAGFFFzDWUR4hLT7JpF" aria-label="Solscan">
            Solscan
          </FooterLink>
          <FooterLink href="https://app.uniswap.org/explore/tokens/ethereum/0x473f4068073cd5b2ab0e4cc8e146f9edc6fb52cc" aria-label="Uniswap - NUT on Ethereum">
            Uniswap
          </FooterLink>
          <FooterLink href="https://www.digifinex.com/en-ww/trade/USDT/NUT" aria-label="Digifinex - NUT/USDT">
            Digifinex
          </FooterLink>
        </FooterSection>
      </SimpleGrid>

      <Box mt="3.6rem" fontSize={{ base: "0.82rem", sm: "0.82rem", lg: "0.82rem" }}>
        <Translation id="copyright" />
      </Box>
    </BoxContainer>
  )
}

export default Footer
